/**
 * Calcolo della data di rinnovo, lato interfaccia.
 *
 * ⚠️ **Questa logica esiste anche nel backend** (`backend/src/lib/date.ts`,
 * funzione `aggiungiMesi`). È una duplicazione consapevole: il dialogo di
 * completamento deve mostrare la data proposta mentre l'utente cambia la
 * data di esecuzione, e chiedere al server a ogni battuta sarebbe lento e
 * fragile.
 *
 * **Chi modifica una delle due deve modificare anche l'altra**, e rieseguire
 * i test di entrambe. Il backend resta comunque l'autorità: la data che vale
 * è quella che salva lui, questa è solo l'anteprima.
 *
 * La convenzione sui mesi corti è la stessa: 31 gennaio + 1 mese non esiste,
 * si ripiega sull'ultimo giorno del mese (28 o 29 febbraio). Corrisponde
 * all'intuizione di chi gestisce scadenze: una verifica fatta a fine mese si
 * rinnova a fine mese.
 */

export function aggiungiMesi(data: string, mesi: number): string {
  const [annoStr, meseStr, giornoStr] = data.split('-');
  const anno = Number(annoStr);
  const mese = Number(meseStr);
  const giorno = Number(giornoStr);

  if (!Number.isInteger(anno) || !Number.isInteger(mese) || !Number.isInteger(giorno)) {
    throw new Error(`Data non valida: ${data}`);
  }

  const totaleMesi = anno * 12 + (mese - 1) + mesi;
  const nuovoAnno = Math.floor(totaleMesi / 12);
  const nuovoMese = (totaleMesi % 12) + 1;

  // Giorno 0 del mese successivo = ultimo giorno del mese corrente.
  const giorniNelMese = new Date(Date.UTC(nuovoAnno, nuovoMese, 0)).getUTCDate();
  const nuovoGiorno = Math.min(giorno, giorniNelMese);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${nuovoAnno}-${pad(nuovoMese)}-${pad(nuovoGiorno)}`;
}

/**
 * Data proposta per il rinnovo, o `null` se il tipo non è ricorrente.
 *
 * Si conta dalla data in cui la scadenza è stata **effettivamente fatta**,
 * non da quella in cui era prevista: la validità di una visita medica o di
 * una taratura decorre da quando è stata eseguita.
 */
export function proponiRinnovo(
  dataCompletamento: string,
  periodicitaMesi: number | null,
): string | null {
  if (periodicitaMesi === null) return null;
  return aggiungiMesi(dataCompletamento, periodicitaMesi);
}

/** "ogni 60 mesi" · "ogni 12 mesi" · "una tantum" */
export function descriviPeriodicita(periodicitaMesi: number | null): string {
  if (periodicitaMesi === null) return 'una tantum';
  return `ogni ${periodicitaMesi} mesi`;
}
